import { useState, useCallback, useEffect } from 'react'
import { vehiclePositionService } from '../services/api/vehiclePositions'
import { markerService } from '../services/map/markers'
import type { VehiclePositionWithDescriptors, TransitServiceType } from '../types/api'

interface UseVehiclePositionsOptions {
  autoRefresh?: boolean
  refreshInterval?: number
  serviceType?: TransitServiceType
}

export const useVehiclePositions = (options: UseVehiclePositionsOptions = {}) => {
  const {
    autoRefresh = true,
    refreshInterval = 30000,
    serviceType = 'sydney-metro'
  } = options

  const [vehicles, setVehicles] = useState<VehiclePositionWithDescriptors[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [lastUpdate, setLastUpdate] = useState<string>('')
  const [error, setError] = useState<string | null>(null)

  const fetchVehicles = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      let response
      if (serviceType === 'sydney-metro') {
        response = await vehiclePositionService.getSydneyMetroPositions()
      } else if (serviceType === 'sydney-trains') {
        response = await vehiclePositionService.getSydneyTrainsPositions()
      } else if (serviceType === 'light-rail-innerwest') {
        response = await vehiclePositionService.getLightRailInnerwestPositions()
      } else {
        throw new Error(`Unsupported service type: ${serviceType}`)
      }

      if (response.success && response.data?.vehicle_positions) {
        setVehicles(response.data.vehicle_positions)
        setLastUpdate(new Date().toLocaleTimeString())

        // Update markers on the map
        markerService.addVehicleMarkers(response.data.vehicle_positions, serviceType)
      }
    } catch (err) {
      console.error('Failed to fetch vehicle positions:', err)
      setError(err instanceof Error ? err.message : 'Failed to fetch vehicle positions')
    } finally {
      setIsLoading(false)
    }
  }, [serviceType])

  // Fetch data when service type changes. The setState inside fetchVehicles
  // (setIsLoading → setVehicles) is the entire point of this effect; the
  // react-hooks/set-state-in-effect rule is a false positive for this
  // standard fetch-on-mount/change pattern.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchVehicles()
  }, [fetchVehicles])

  // Set up auto-refresh
  useEffect(() => {
    if (!autoRefresh) return

    const interval = setInterval(fetchVehicles, refreshInterval)
    return () => clearInterval(interval)
  }, [fetchVehicles, autoRefresh, refreshInterval])

  return {
    vehicles,
    isLoading,
    lastUpdate,
    error,
    fetchVehicles,
    vehicleCount: vehicles.length
  }
}
